import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { Button } from '@lwt-helix/buttons';
import Modal from '@lwt-helix/modal';
import { HelixIcon } from '@lwt-helix/helix-icon';
import { plus } from '@lwt-helix/helix-icon/outlined';
import { CustomInput } from '@lwt-helix/controls';
import { useCommonStore, useSearchStore } from '../../../store/store';
import {
    favoriteAgentTerms as favTerms,
    popoverTerms
} from '../../constants/agentProductionConstants';
import { buttonTerms } from '../../../constants';
import { useSavedAgentsStore }  from '../../../store/savedAgents/store';
import { getSelectedAgentIds } from '../../../common/components/table/agGrid/crossViewSelection';
import NewAgentListModal from './NewAgentListModal';

const SavedAgentListsModal = ({ shouldShowModal, onClose, selectedCount, setToastProps }) => {
    const {
        agentLists,
    } = useCommonStore(state => ({
        agentLists: state.agentLists,
    }));
    const {
        mlsId,
        selectedAgentIds,
    } = useSearchStore(state => ({
        mlsId: state[state.selectedModule]?.search?.mlsId,
        selectedAgentIds: getSelectedAgentIds(state) ?? [],
    }));
    const {
        setSavedAgentList,
    } = useSavedAgentsStore(state => ({
        setSavedAgentList: state.setSavedAgentList,
    }));
    const [checkedListIds, setCheckedListIds] = useState({});
    const [isSaveBtnDisable, setIsSaveBtnDisable] = useState(false);
    const [showNewListModal, setShowNewListModal] = useState(false);

    const filteredLists = agentLists?.filter(list => list?.mlsId === mlsId) || [];
    const hasLists = filteredLists.length > 0;
    const SAVED_AGENTS_LIST_MAX_LIMIT = 2000;
    useEffect(() => {
        if (shouldShowModal) {
            setCheckedListIds({});
            setShowNewListModal(false);
        }
    }, [shouldShowModal]);

    useEffect(() => {
        setIsSaveBtnDisable(Object.values(checkedListIds).includes(true));
    }, [checkedListIds]);

    const handleClose = () => {
        setCheckedListIds({});
        setShowNewListModal(false);
        onClose();
    };

    const handleSave = () => {
        const savedAgentListIds = Object.keys(checkedListIds).filter(listId => !!checkedListIds[listId]);
        if (isSaveBtnDisable) {
            setSavedAgentList({ savedAgentListIds, selectedCount, selectedAgentIds, setToastProps });
            onClose();
        }
    };

    const handleCheckboxChange = (listId) => {
        setCheckedListIds(prev => ({
            ...prev,
            [listId]: !prev[listId]
        }));
    };

    const renderedCheckboxes = filteredLists.map(list => {
        const agentListCount = list?.agentIds?.length;
        const { countWithLabel } = favTerms.addingContactsTo(agentListCount);
        const label = `${list?.name} (${countWithLabel})`;
        const isDisabled = agentListCount === SAVED_AGENTS_LIST_MAX_LIMIT ||
            agentListCount + selectedCount > SAVED_AGENTS_LIST_MAX_LIMIT;
        return (
            <div key={list?.agentListId} className='pb-3 px-2'>
                <CustomInput
                    checked={checkedListIds[list?.agentListId] || false}
                    id={`saved-list-${list?.agentListId}`}
                    label={label}
                    onChange={() => handleCheckboxChange(list?.agentListId)}
                    type={popoverTerms.checkbox}
                    disabled={isDisabled}
                />
            </div>
        );
    });

    const modalButtons = (
        <>
            <Button
                dataLwtId='cancel-saved-agent-lists'
                color='secondary'
                onClick={handleClose}
            >
                {buttonTerms.cancel}
            </Button>
            <Button
                dataLwtId='save-saved-agent-lists'
                color='primary'
                onClick={handleSave}
                disabled={!isSaveBtnDisable}
            >
                {buttonTerms.save}
            </Button>
        </>
    );

    const { prefix, countWithLabel, suffix } = favTerms.addingContactsTo(selectedCount);
    const modalChildren = (
        <div>
            <div className='d-flex justify-content-between align-items-center mb-2'>
                {hasLists ? (
                    <span>
                        {prefix} <span className='font-weight-bold'>{countWithLabel}</span> {suffix}
                    </span>
                ) : (
                    <span className='font-weight-bold ml-auto mr-3'>
                        {favTerms.createSavedAgentList}
                    </span>
                )}
                <Button
                    dataLwtId='new-list-button'
                    color='secondary'
                    className='text-capitalize d-flex align-items-center'
                    onClick={() => setShowNewListModal(true)}
                >
                    <HelixIcon className='mr-1' icon={plus} dataLwtId='new-list-icon' />
                    {favTerms.newList}
                </Button>
            </div>
            <hr className='my-2'/>
            {hasLists ? (
                <div
                    className='saved-agent-lists-checkboxes overflow-auto'
                >
                    {renderedCheckboxes}
                </div>
            ) : (
                <p className='font-weight-normal'>
                    {favTerms.emptyListDescription}
                </p>
            )}
            <hr className='my-2'/>
        </div>
    );

    const modalTitle = (
        <>
            <p className='mb-0'>{favTerms.savedAgentListsTitle}</p>
            <p className='modal-subtitle'>{favTerms.savedAgentsLimitsToAddMssg}</p>
        </>
    );
    return (
        <>
            <Modal
                buttons={modalButtons}
                onClose={handleClose}
                show={shouldShowModal && !showNewListModal}
                title={modalTitle}
            >
                {modalChildren}
            </Modal>
            <NewAgentListModal
                isModalOpen={showNewListModal}
                setShowNewListModal={setShowNewListModal}
                onClose={() => setShowNewListModal(false)}
                setToastProps={setToastProps}
                setShowAddToListModal={onClose}
            />
        </>
    );
};

SavedAgentListsModal.propTypes = {
    setToastProps: PropTypes.func,
    shouldShowModal: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    selectedCount: PropTypes.number
};

export default SavedAgentListsModal;
